// Сессии испытаний (десктоп) — список сессий стенда мембран, а для выбранной
// сессии: агрегаты по телеметрии, график давления/температуры и таблица
// зачётных замеров.
//
// Сессию открывает оператор с мобилы или сам стенд (автостарт).  Пока
// ended_at = null — сессия идёт, детали и график перезапрашиваются раз в 10 с.
//
// Sidecar-модуль: endpoints /membrane-rig/* и /membrane-tests, requireAuth.
// График — тот же лёгкий inline-SVG (без сторонних библиотек), что и на
// странице «Испытания мембран», но с разрывами линии на null-точках.

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components/PageHeader";
import { membraneRigApi, membraneTestsApi } from "@/lib/api";
import type {
  MembraneTestRead,
  RigSamplesResponse,
  RigSessionDetail,
  RigSessionRead,
  RigSessionStatus,
} from "@/lib/api";
import type { Page } from "@/lib/types";
import { formatDateTime, formatTime } from "@/lib/format";

const PAGE_SIZE = 50;

// Телеметрия приходит в МПа, а весь интерфейс испытаний живёт в барах.
const MPA_TO_BAR = 10;

// Метрика графика: давление (бар) или температура (°C).
type ChartMetric = "pressure" | "temperature";

/** Длительность сессии в компактном виде: «ЧЧ:ММ:СС», а от суток — «Nд ЧЧ:ММ».
 *  Единицу суток передаём из i18n, остальное — цифры и двоеточия. */
function formatDuration(ms: number, dayUnit: string): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return days > 0
    ? `${days}${dayUnit} ${pad(hours)}:${pad(minutes)}`
    : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

/** МПа → бар для показа; null (датчик в ошибке / нет данных) → «—». */
function barText(mpa: number | null | undefined): string {
  return mpa == null ? "—" : (mpa * MPA_TO_BAR).toFixed(2);
}

/** °C → строка с одним знаком; null → «—». */
function tempText(c: number | null | undefined): string {
  return c == null ? "—" : c.toFixed(1);
}

/** л/мин → строка с одним знаком; null → «—». */
function lpmText(q: number | null | undefined): string {
  return q == null ? "—" : q.toFixed(1);
}

/** Пик мгновенного расхода (л/мин) → прогнозный суточный, м³/сут — та же
 *  цифра, что «храповик» на дашборде стенда. */
function dailyText(peakLpm: number | null | undefined): string {
  return peakLpm == null ? "—" : ((peakLpm * 1440) / 1000).toFixed(1);
}

export default function MembraneSessions() {
  const { t } = useTranslation();

  const [status, setStatus] = useState<RigSessionStatus | "">("");
  const [page, setPage] = useState(1);
  // Сессия, раскрытая в блоке деталей (null — ничего не выбрано).
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const sessions = useQuery({
    queryKey: ["membrane-rig", "sessions", status, page],
    queryFn: () =>
      membraneRigApi.sessions({
        page,
        page_size: PAGE_SIZE,
        ...(status ? { status } : {}),
      }),
    refetchInterval: 15_000,
  });

  const rows = sessions.data?.items ?? [];
  const selectedRow = rows.find((r) => r.id === selectedId) ?? null;

  // Идущую сессию (ended_at = null) перезапрашиваем раз в 10 с — статус
  // берём из уже полученных данных самого запроса.
  const detail = useQuery({
    queryKey: ["membrane-rig", "session", selectedId],
    queryFn: () => membraneRigApi.session(selectedId!),
    enabled: selectedId !== null,
    refetchInterval: (query) =>
      query.state.data?.ended_at === null ? 10_000 : false,
  });

  // «Живая» сессия: ended_at ещё не проставлен.  Пока карточка деталей не
  // загрузилась, ориентируемся на строку из списка.
  const live = (detail.data ?? selectedRow)?.ended_at === null;

  const samples = useQuery({
    queryKey: ["membrane-rig", "samples", selectedId],
    queryFn: () => membraneRigApi.samples({ session_id: selectedId!, max_points: 1000 }),
    enabled: selectedId !== null,
    refetchInterval: live ? 10_000 : false,
  });

  const sessionTests = useQuery({
    queryKey: ["membrane-tests", "session", selectedId],
    queryFn: () =>
      membraneTestsApi.list({ session_id: selectedId!, page_size: 500 }),
    enabled: selectedId !== null,
    refetchInterval: live ? 10_000 : false,
  });

  return (
    <>
      <PageHeader
        eyebrow="membrane-rig"
        title={t("membraneSessions.title")}
        description={t("membraneSessions.description")}
      />

      {/* Фильтры. */}
      <section className="flex flex-wrap items-end gap-4 mt-6">
        <label className="flex flex-col gap-1">
          <span className="text-text-muted text-xs font-mono uppercase tracking-wider">
            {t("membraneSessions.status")}
          </span>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as RigSessionStatus | "");
              setPage(1);
            }}
            className="px-3 py-2 rounded-md bg-surface border border-border text-sm min-w-[200px]"
          >
            <option value="">{t("membraneSessions.statusAll")}</option>
            <option value="open">{t("membraneSessions.statusOpen")}</option>
            <option value="closed">{t("membraneSessions.statusClosed")}</option>
          </select>
        </label>

        <span className="text-text-muted text-sm ml-auto tabular-nums">
          {t("membraneSessions.count", {
            count: sessions.data?.pagination.total ?? rows.length,
          })}
        </span>
      </section>

      {/* Список сессий. */}
      <section className="card overflow-x-auto mt-4">
        {sessions.isLoading ? (
          <p className="p-6 text-text-muted text-sm">{t("common.loading")}</p>
        ) : rows.length === 0 ? (
          <p className="p-6 text-text-muted text-sm">
            {t("membraneSessions.empty")}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-3 py-3">{t("membraneSessions.colStarted")}</th>
                <th className="px-3 py-3 text-right">
                  {t("membraneSessions.colDuration")}
                </th>
                <th className="px-3 py-3">{t("membraneTests.model")}</th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.colArea")}
                </th>
                <th className="px-3 py-3">{t("membraneTests.serial")}</th>
                <th className="px-3 py-3 text-right">{t("membraneSessions.statPAvg")}</th>
                <th className="px-3 py-3 text-right">{t("membraneSessions.colFeedDaily")}</th>
                <th className="px-3 py-3 text-right">{t("membraneSessions.colPermeateDaily")}</th>
                <th className="px-3 py-3">
                  {t("membraneSessions.colStartedBy")}
                </th>
                <th className="px-3 py-3">{t("common.status")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <SessionRow
                  key={r.id}
                  row={r}
                  selected={r.id === selectedId}
                  onSelect={() =>
                    setSelectedId((cur) => (cur === r.id ? null : r.id))
                  }
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Пагинация. */}
      {sessions.data && sessions.data.pagination.pages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm">
          <span className="text-text-muted">
            {t("common.pageOf", {
              page: sessions.data.pagination.page,
              pages: sessions.data.pagination.pages,
              total: sessions.data.pagination.total,
            })}
          </span>
          <div className="flex gap-2">
            <button
              className="btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ←
            </button>
            <button
              className="btn"
              disabled={page >= sessions.data.pagination.pages}
              onClick={() => setPage((p) => p + 1)}
            >
              →
            </button>
          </div>
        </div>
      )}

      {/* Детали выбранной сессии. */}
      {selectedId === null ? (
        <p className="text-text-muted text-sm mt-6">
          {t("membraneSessions.selectHint")}
        </p>
      ) : (
        <SessionDetails
          detailQuery={detail}
          samplesQuery={samples}
          testsQuery={sessionTests}
          live={live}
        />
      )}
    </>
  );
}

// ───────── Строка списка сессий ─────────

function SessionRow({
  row,
  selected,
  onSelect,
}: {
  row: RigSessionRead;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const running = row.ended_at === null;
  // Для идущей сессии длительность считаем «до сейчас» — значение обновится
  // на следующем перезапросе списка.
  const durationMs =
    new Date(row.ended_at ?? Date.now()).getTime() -
    new Date(row.started_at).getTime();

  return (
    <tr
      className={`border-t border-border cursor-pointer ${
        selected ? "bg-surface-hover" : "hover:bg-surface-hover"
      }`}
      onClick={onSelect}
    >
      <td className="px-3 py-3 text-text-muted font-mono text-xs whitespace-nowrap">
        {formatDateTime(row.started_at)}
      </td>
      <td className="px-3 py-3 text-right tabular-nums font-mono text-xs whitespace-nowrap">
        {formatDuration(durationMs, t("membraneSessions.unitDay"))}
      </td>
      <td className="px-3 py-3 font-medium">{row.membrane_model ?? "—"}</td>
      <td className="px-3 py-3 text-right tabular-nums font-mono text-text-muted">
        {row.area ?? "—"}
      </td>
      <td className="px-3 py-3 font-mono text-xs text-text-muted">
        {row.serial ?? "—"}
      </td>
      {/* Итоги, записанные при стопе; у идущей сессии их ещё нет. */}
      <td className="px-3 py-3 text-right tabular-nums font-mono">
        {barText(row.p_mpa_avg)}
      </td>
      <td className="px-3 py-3 text-right tabular-nums font-mono">
        {dailyText(row.q1_lpm_peak)}
      </td>
      <td className="px-3 py-3 text-right tabular-nums font-mono">
        {dailyText(row.q2_lpm_peak)}
      </td>
      <td className="px-3 py-3 text-text-secondary">
        {row.started_by === "operator"
          ? t("membraneSessions.startedByOperator")
          : t("membraneSessions.startedByRig")}
      </td>
      <td className="px-3 py-3 whitespace-nowrap">
        <span className={running ? "pill pill-accent" : "pill pill-ok"}>
          {running
            ? t("membraneSessions.running")
            : t("membraneSessions.finished")}
        </span>
      </td>
    </tr>
  );
}

// ───────── Детали выбранной сессии ─────────

type DetailQuery = UseQueryResult<RigSessionDetail, Error>;
type SamplesQuery = UseQueryResult<RigSamplesResponse, Error>;
type TestsQuery = UseQueryResult<Page<MembraneTestRead>, Error>;

function SessionDetails({
  detailQuery,
  samplesQuery,
  testsQuery,
  live,
}: {
  detailQuery: DetailQuery;
  samplesQuery: SamplesQuery;
  testsQuery: TestsQuery;
  live: boolean;
}) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<ChartMetric>("pressure");

  const session = detailQuery.data;
  const tests = useMemo(() => testsQuery.data?.items ?? [], [testsQuery.data]);

  // Риски зачётных замеров на графике — по времени внесения замера.
  const markers = useMemo(
    () => tests.map((r) => new Date(r.created_at).getTime()),
    [tests],
  );

  // Точки графика: давление переводим МПа → бар, null оставляем как есть —
  // это разрыв линии (датчик был в ошибке), а не ноль.
  const points = useMemo(() => {
    const raw = samplesQuery.data?.points ?? [];
    return raw.map((p) => ({
      x: new Date(p.ts).getTime(),
      y:
        metric === "pressure"
          ? p.p_mpa == null
            ? null
            : p.p_mpa * MPA_TO_BAR
          : p.t_c,
    }));
  }, [samplesQuery.data, metric]);

  if (detailQuery.isLoading) {
    return <p className="text-text-muted text-sm mt-6">{t("common.loading")}</p>;
  }
  if (!session) {
    return (
      <p className="text-text-muted text-sm mt-6">
        {t("membraneSessions.notFound")}
      </p>
    );
  }

  const durationMs =
    new Date(session.ended_at ?? Date.now()).getTime() -
    new Date(session.started_at).getTime();
  const metricUnit =
    metric === "pressure"
      ? t("membraneSessions.unitBar")
      : t("membraneSessions.unitCelsius");

  return (
    <>
      {/* Шапка сессии + агрегаты. */}
      <section className="card p-5 mt-6">
        <header className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-sm font-mono uppercase tracking-wider text-text-muted">
            {t("membraneSessions.detailsTitle")} ·{" "}
            {formatDateTime(session.started_at)}
          </h2>
          <span className="text-text-muted text-xs font-mono">
            {session.rig_id} ·{" "}
            {formatDuration(durationMs, t("membraneSessions.unitDay"))}
            {live ? ` · ${t("membraneSessions.liveHint")}` : ""}
          </span>
        </header>

        <dl className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mt-4">
          <Stat
            label={t("membraneSessions.statSamples")}
            value={String(session.stats.samples)}
          />
          <Stat
            label={t("membraneSessions.statPMin")}
            value={barText(session.stats.p_min)}
          />
          <Stat
            label={t("membraneSessions.statPAvg")}
            value={barText(session.stats.p_avg)}
          />
          <Stat
            label={t("membraneSessions.statPMax")}
            value={barText(session.stats.p_max)}
          />
          <Stat
            label={t("membraneSessions.statTMin")}
            value={tempText(session.stats.t_min)}
          />
          <Stat
            label={t("membraneSessions.statTAvg")}
            value={tempText(session.stats.t_avg)}
          />
          <Stat
            label={t("membraneSessions.statTMax")}
            value={tempText(session.stats.t_max)}
          />
        </dl>

        {/* Расходы: мгновенный (последний) и пик за сессию → прогнозный суточный. */}
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <Stat
            label={t("membraneSessions.statFeedLast")}
            value={lpmText(session.stats.q1_lpm_last)}
          />
          <Stat
            label={t("membraneSessions.statFeedDaily")}
            value={dailyText(session.stats.q1_lpm_peak)}
          />
          <Stat
            label={t("membraneSessions.statPermeateLast")}
            value={lpmText(session.stats.q2_lpm_last)}
          />
          <Stat
            label={t("membraneSessions.statPermeateDaily")}
            value={dailyText(session.stats.q2_lpm_peak)}
          />
        </dl>

        {/* Паспорт сессии: то, что внёс оператор при старте. */}
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
          <Stat
            label={t("membraneTests.model")}
            value={session.membrane_model ?? "—"}
            mono={false}
          />
          <Stat
            label={t("membraneTests.serial")}
            value={session.serial ?? "—"}
          />
          <Stat
            label={t("membraneTests.metrics.feed_tds")}
            value={session.feed_tds == null ? "—" : String(session.feed_tds)}
          />
          <Stat
            label={t("membraneTests.metrics.feed_conductivity")}
            value={
              session.feed_conductivity == null
                ? "—"
                : String(session.feed_conductivity)
            }
          />
        </dl>

        {session.note && (
          <p className="text-text-secondary text-sm mt-3">{session.note}</p>
        )}
      </section>

      {/* График телеметрии. */}
      <section className="card p-5 mt-4">
        <header className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-sm font-mono uppercase tracking-wider text-text-muted">
            {metric === "pressure"
              ? t("membraneSessions.metricPressure")
              : t("membraneSessions.metricTemperature")}{" "}
            · {metricUnit}
          </h2>
          <div className="flex gap-2">
            <button
              className={metric === "pressure" ? "btn btn-primary" : "btn"}
              onClick={() => setMetric("pressure")}
            >
              {t("membraneSessions.metricPressure")}
            </button>
            <button
              className={metric === "temperature" ? "btn btn-primary" : "btn"}
              onClick={() => setMetric("temperature")}
            >
              {t("membraneSessions.metricTemperature")}
            </button>
          </div>
        </header>

        {samplesQuery.isLoading ? (
          <p className="text-text-muted text-sm py-10 text-center">
            {t("common.loading")}
          </p>
        ) : (
          <RigChart points={points} unit={metricUnit} markers={markers} />
        )}

        {samplesQuery.data && samplesQuery.data.points.length > 0 && (
          <p className="text-text-muted text-xs font-mono mt-2">
            {t("membraneSessions.bucketHint", {
              seconds: samplesQuery.data.bucket_seconds,
              points: samplesQuery.data.points.length,
            })}
          </p>
        )}
      </section>

      {/* Зачётные замеры сессии. */}
      <section className="card overflow-x-auto mt-4">
        <h2 className="text-sm font-mono uppercase tracking-wider text-text-muted px-5 pt-5">
          {t("membraneSessions.testsTitle")}
        </h2>
        {testsQuery.isLoading ? (
          <p className="p-5 text-text-muted text-sm">{t("common.loading")}</p>
        ) : tests.length === 0 ? (
          <p className="p-5 text-text-muted text-sm">
            {t("membraneSessions.noTests")}
          </p>
        ) : (
          <table className="w-full text-sm mt-3">
            <thead className="bg-surface-alt">
              <tr className="text-left text-text-muted font-mono text-xs uppercase">
                <th className="px-3 py-3">{t("membraneSessions.colTime")}</th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.pressure_in")}
                </th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.pressure_out")}
                </th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.permeate_pressure")}
                </th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.concentrate_flow")}
                </th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.permeate_flow")}
                </th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.feed_tds")}
                </th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.tds")}
                </th>
                <th className="px-3 py-3 text-right">
                  {t("membraneTests.metrics.temperature")}
                </th>
              </tr>
            </thead>
            <tbody>
              {tests.map((r) => (
                <tr key={r.id} className="border-t border-border">
                  <td className="px-3 py-3 text-text-muted font-mono text-xs whitespace-nowrap">
                    {formatDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.pressure_in}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.pressure_out}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.permeate_pressure}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.concentrate_flow}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.permeate_flow}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.feed_tds ?? "—"}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.tds ?? "—"}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    {r.temperature}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/** Плитка агрегата: подпись сверху, значение моноширинным. */
function Stat({
  label,
  value,
  mono = true,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="rounded-md bg-surface-alt px-3 py-2">
      <dt className="text-text-muted text-[10px] font-mono uppercase tracking-wider">
        {label}
      </dt>
      <dd
        className={`mt-0.5 text-sm ${
          mono ? "tabular-nums font-mono" : "font-medium"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

// ───────── Лёгкий inline-SVG график телеметрии (без зависимостей) ─────────
//
// Отличие от графика на «Испытаниях мембран»: y может быть null — датчик был
// в ошибке в этом бакете.  Такие точки РВУТ линию: путь режется на сегменты,
// нулей и интерполяции не появляется.

function RigChart({
  points,
  unit,
  markers,
}: {
  points: Array<{ x: number; y: number | null }>;
  unit: string;
  markers: number[];
}) {
  const { t } = useTranslation();
  const W = 900;
  const H = 280;
  const pad = { top: 16, right: 16, bottom: 28, left: 48 };

  const valid = points.filter(
    (p): p is { x: number; y: number } =>
      p.y !== null && Number.isFinite(p.y),
  );

  if (valid.length === 0) {
    return (
      <p className="text-text-muted text-sm py-10 text-center">
        {t("membraneSessions.noSamples")}
      </p>
    );
  }

  // Ось X держим по ВСЕМ точкам (включая пропуски), иначе провал в конце
  // ряда визуально «схлопнется».
  const xs = points.map((p) => p.x);
  const ys = valid.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  let minY = Math.min(...ys);
  let maxY = Math.max(...ys);
  // Небольшой запас сверху/снизу + защита от плоской линии.
  if (minY === maxY) {
    minY -= 1;
    maxY += 1;
  } else {
    const m = (maxY - minY) * 0.08;
    minY -= m;
    maxY += m;
  }

  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;

  const sx = (x: number) =>
    pad.left + (maxX === minX ? innerW / 2 : ((x - minX) / (maxX - minX)) * innerW);
  const sy = (y: number) =>
    pad.top + innerH - ((y - minY) / (maxY - minY)) * innerH;

  // Режем ряд на непрерывные сегменты: каждый null закрывает текущий сегмент.
  const segments: Array<Array<{ x: number; y: number }>> = [];
  let current: Array<{ x: number; y: number }> = [];
  for (const p of points) {
    if (p.y === null || !Number.isFinite(p.y)) {
      if (current.length > 0) {
        segments.push(current);
        current = [];
      }
      continue;
    }
    current.push({ x: p.x, y: p.y });
  }
  if (current.length > 0) segments.push(current);

  // 4 горизонтальные сетки + подписи по Y.
  const ticks = Array.from({ length: 5 }, (_, i) => minY + ((maxY - minY) * i) / 4);

  // Точки-кружки рисуем только на коротких рядах — на 1000 бакетов это
  // тысяча DOM-узлов без пользы.
  const showDots = valid.length <= 150;

  // Риски зачётных замеров — только те, что попали во временной диапазон.
  const marks = markers.filter((m) => m >= minX && m <= maxX);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      style={{ maxHeight: 320 }}
      role="img"
    >
      {ticks.map((tv, i) => {
        const y = sy(tv);
        return (
          <g key={i}>
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={y}
              y2={y}
              stroke="var(--color-border)"
              strokeWidth={1}
            />
            <text
              x={pad.left - 8}
              y={y + 3}
              textAnchor="end"
              fontSize={10}
              fontFamily="var(--font-mono)"
              fill="var(--color-text-muted)"
            >
              {tv.toFixed(maxY - minY < 5 ? 1 : 0)}
            </text>
          </g>
        );
      })}

      {/* Вертикальные риски — моменты зачётных замеров. */}
      {marks.map((m, i) => (
        <line
          key={`mark-${i}`}
          x1={sx(m)}
          x2={sx(m)}
          y1={pad.top}
          y2={pad.top + innerH}
          stroke="var(--color-text-muted)"
          strokeWidth={1}
          strokeDasharray="3 3"
          opacity={0.6}
        >
          <title>{formatDateTime(new Date(m).toISOString())}</title>
        </line>
      ))}

      {segments.map((seg, i) => (
        <path
          key={`seg-${i}`}
          d={seg
            .map(
              (p, j) =>
                `${j === 0 ? "M" : "L"}${sx(p.x).toFixed(1)},${sy(p.y).toFixed(1)}`,
            )
            .join(" ")}
          fill="none"
          stroke="var(--color-accent-strong)"
          strokeWidth={2}
        />
      ))}

      {/* Одиночные точки между разрывами: путь из одного узла ничего не
          рисует, поэтому подсвечиваем их кружком всегда. */}
      {segments
        .filter((seg) => seg.length === 1)
        .map((seg, i) => (
          <circle
            key={`solo-${i}`}
            cx={sx(seg[0].x)}
            cy={sy(seg[0].y)}
            r={2.5}
            fill="var(--color-accent-strong)"
          />
        ))}

      {showDots &&
        valid.map((p, i) => (
          <circle
            key={`dot-${i}`}
            cx={sx(p.x)}
            cy={sy(p.y)}
            r={3.5}
            fill="var(--color-accent-strong)"
          >
            <title>
              {formatDateTime(new Date(p.x).toISOString())}: {p.y.toFixed(2)}{" "}
              {unit}
            </title>
          </circle>
        ))}

      {/* Подписи краёв диапазона по X. */}
      <text
        x={pad.left}
        y={H - 8}
        fontSize={10}
        fontFamily="var(--font-mono)"
        fill="var(--color-text-muted)"
      >
        {formatTime(new Date(minX).toISOString())}
      </text>
      <text
        x={W - pad.right}
        y={H - 8}
        textAnchor="end"
        fontSize={10}
        fontFamily="var(--font-mono)"
        fill="var(--color-text-muted)"
      >
        {formatTime(new Date(maxX).toISOString())}
      </text>
    </svg>
  );
}
